import { missingWords } from '@/lib/rowTexts.js'
/**
 * Helpers over the offer document tree (OfferDocument on the server): find, walk, replace a
 * block immutably, list the blocks that miss a translation.
 */
// Blocks an offer fills in itself: never copied into another (D-190).
export const BLOCKS_OWN = ['title', 'site_address', 'info_boxes', 'revision_history', 'terms', 'toc', 'documents']

export function walk(blocks, fn, parent = null) {
  for (const block of blocks || []) {
    fn(block, parent)
    walk(block.children, fn, block)
  }
}

export function find(blocks, uuid) {
  let found = null
  walk(blocks, (b) => {
    if (b.uuid === uuid) found = b
  })
  return found
}

/*
 * Changes keep what they do not touch: a block, a list of blocks unchanged stay the same objects,
 * only the way to what changed is new — what draws and compares the document skips the rest (an
 * offer has hundreds of blocks; a key typed changes one).
 */
function within(blocks, fn) {
  const list = blocks || []
  let changed = false
  const out = []
  for (const b of list) {
    const next = fn(b)
    if (next !== b) changed = true
    if (Array.isArray(next)) out.push(...next)
    else if (next) out.push(next)
  }
  return changed ? out : blocks
}
const withChildren = (b, children) => (children === b.children ? b : { ...b, children })

export function update(blocks, uuid, patch) {
  return within(blocks, (b) => (b.uuid === uuid ? { ...b, ...patch } : withChildren(b, update(b.children, uuid, patch))))
}

export function remove(blocks, uuid) {
  return within(blocks, (b) => (b.uuid === uuid ? null : withChildren(b, remove(b.children, uuid))))
}

export function move(blocks, uuid, delta) {
  const list = [...(blocks || [])]
  const i = list.findIndex((b) => b.uuid === uuid)
  if (i >= 0) {
    const j = i + delta
    if (j < 0 || j >= list.length) return blocks || []
    ;[list[i], list[j]] = [list[j], list[i]]
    return list
  }
  return within(blocks, (b) => withChildren(b, move(b.children, uuid, delta)))
}

export function insertAfter(blocks, uuid, block) {
  return within(blocks, (b) => (b.uuid === uuid ? [withChildren(b, insertAfter(b.children, uuid, block)), block] : withChildren(b, insertAfter(b.children, uuid, block))))
}

export function insertBefore(blocks, uuid, block) {
  return within(blocks, (b) => (b.uuid === uuid ? [block, withChildren(b, insertBefore(b.children, uuid, block))] : withChildren(b, insertBefore(b.children, uuid, block))))
}

/** A block moved before or after another one (anywhere in the tree), never into itself. */
export function moveTo(blocks, uuid, target, place) {
  const moved = find(blocks, uuid)
  if (!moved || uuid === target || find([moved], target)) return blocks
  const rest = remove(blocks, uuid)
  return place === 'before' ? insertBefore(rest, target, moved) : insertAfter(rest, target, moved)
}

/** The text of a block in a language: headings and groups carry {pl,en} strings, sections and texts TipTap documents. */
export function contentIn(block, lang) {
  const c = block.content
  if (!c || typeof c !== 'object') return null
  return c[lang] ?? null
}

export function hasText(value) {
  if (!value) return false
  if (typeof value === 'string') return value.trim().length > 0
  let any = false
  const scan = (node) => {
    if (any || !node || typeof node !== 'object') return
    if (node.type === 'text' && String(node.text || '').trim()) any = true
    ;(node.content || []).forEach(scan)
  }
  scan(value)
  return any
}

/** Visible blocks whose text is missing in the offer's language — freezing would be refused. */
export function missingTranslations(blocks, lang, base = null) {
  const out = []
  const visit = (list, hiddenAbove) => {
    for (const b of list || []) {
      const hidden = hiddenAbove || b.hidden || b.replaced_by
      if (!hidden && ['heading', 'text', 'section', 'group'].includes(b.kind) && b.content && typeof b.content === 'object' && Object.keys(b.content).length && !hasText(contentIn(b, lang))) {
        out.push(b.uuid)
      }
      // A row's words without this language's (D-303) — equipment priced in its base prints nothing of its own.
      if (!hidden && base && b.kind === 'row' && !(b.meta?.accessory_of && !b.meta?.separate) && missingWords(b, lang, base).length) out.push(b.uuid)
      visit(b.children, hidden)
    }
  }
  visit(blocks, false)
  return out
}

/** What the server takes back (PUT /offers/{uuid}/blocks): the editable part of each block. */
export function toPayload(blocks) {
  return (blocks || []).map((b) => {
    const node = { uuid: b.uuid, kind: b.kind, hidden: !!b.hidden, children: toPayload(b.children) }
    if (b._contentChanged) node.content = b.content
    if (b._reset) node.reset_to_catalogue = true
    if (b.kind === 'row') Object.assign(node, { values: b.values || {}, translations: b.translations && typeof b.translations === 'object' ? b.translations : {}, quantity: b.quantity, is_optional: !!b.is_optional })
    // A new copy of a catalogue row stays the catalogue's (D-291).
    if (b.kind === 'row' && b.copy_of) node.copy_of = b.copy_of
    // A row an action made in the editor (D-342): the catalogue's as Core signed it, kept by the save.
    if (b.kind === 'row' && b.generated) node.generated = b.generated
    // A summary's manual lines (a discount) are its values (D-148).
    if (b.kind === 'summary' && b.values) node.values = b.values
    // Meta goes back where the editor changes it: a heading's level, a table's columns, a row's "to be agreed", a figure's caption...
    if (b.meta && ['heading', 'table', 'group', 'image', 'row', 'summary', 'figure', 'note', 'legal'].includes(b.kind)) node.meta = b.meta
    if (['heading', 'text', 'group'].includes(b.kind) && !b.source) node.content = b.content
    return node
  })
}

/**
 * The tables an action on the editor's document changed (D-342: equipment, a configuration), put
 * in place of the editor's: their rows as Core answered, each keeping what only the editor knows
 * of it (an edit waiting to be saved, a copy's origin). {table uuid: [row…]}
 */
export function withTables(blocks, tables) {
  return (blocks || []).map((b) => {
    if (tables[b.uuid]) {
      const mine = Object.fromEntries((b.children || []).map((r) => [r.uuid, r]))
      return { ...b, children: tables[b.uuid].map((n) => {
        const local = mine[n.uuid]
        if (!local) return n
        const kept = Object.fromEntries(Object.entries(local).filter(([k]) => k.startsWith('_') || k === 'copy_of' || k === 'generated'))
        return { ...n, ...kept, ...(n.generated ? { generated: n.generated } : {}) }
      }) }
    }
    return b.kind !== 'table' && b.children?.length ? { ...b, children: withTables(b.children, tables) } : b
  })
}
